'use client'

import React from 'react'
import { useColorMode } from '@chakra-ui/react'
import { setAppearance } from '@/lib/appearance/store'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

import { cn } from '@/lib/utils'
import FrozenRouter from '@/components/FrozenRouter'
import AuthBackdrop, { AUTH_GROUND_CLASS } from '@/components/livecontact/AuthBackdrop'
import IconButton from '@/components/livecontact/IconButton'
import Logo from '@/components/livecontact/Logo'
import LcIcon from '@/components/livecontact/icons'
import { EASE_OUT } from '@/components/livecontact/motion'

/**
 * Guest-auth frame: one centred column — big wordmark at the top, the active card
 * underneath, legal line at the foot.
 *
 * The shell is the homepage's: the tinted panel fill with the exported blue glow rising off
 * the bottom edge (one cut per theme, drawn wider than the viewport so the light shafts run
 * off both sides), and the card is the same white surface the hero's assets sit on.
 *
 * Motion: the shell (backdrop, logo, footer) animates once on mount and then stays put;
 * only the card swaps between routes, so moving sign-in → forgot password → reset reads
 * as one surface changing its contents rather than three separate pages.
 */
const LayoutGuestAuth = ({ children }: { children: React.ReactNode }) => {
  const { colorMode, setColorMode } = useColorMode()
  const pathname = usePathname()
  const reduced = Boolean(useReducedMotion())

  /** Enter travels 8px, exit 5px and ~0.6× the duration — the system should answer faster than it announces. */
  const lift = reduced ? 0 : 8

  return (
    <div className={cn('relative flex min-h-[100dvh] flex-col text-ink', AUTH_GROUND_CLASS)}>
      {/* ---------------------------------------------------------------- backdrop */}
      <AuthBackdrop />

      {/* ------------------------------------------------------------------ controls */}
      <div className="absolute right-5 top-5 z-20 flex items-center gap-1 md:right-3 md:top-3">
        <IconButton
          label={`Switch to ${colorMode === 'dark' ? 'light' : 'dark'} mode`}
          shape="square"
          onClick={() => {
            const next = colorMode === 'dark' ? 'light' : 'dark'
            setAppearance({ theme: next })
            setColorMode(next)
          }}
        >
          <LcIcon name={colorMode === 'dark' ? 'moon' : 'sun'} />
        </IconButton>
        <IconButton label="Back to home" shape="square" asChild>
          <Link href="/">
            <LcIcon name="close" />
          </Link>
        </IconButton>
      </div>

      {/* ---------------------------------------------------------------------- body
          Top-anchored rather than vertically centred: the cards differ in height, and
          centring would drop the wordmark by ~35px on every route change. */}
      <main className="relative z-10 flex flex-1 flex-col items-center px-6 pb-16 pt-[max(5rem,13vh)] md:px-4 md:pb-12 md:pt-14">
        <div className="w-full max-w-[420px]">
          {/* CSS, not framer-motion: the shell's entrance is predetermined, so it belongs
              off the main thread and shouldn't depend on a rAF loop to reach its end state. */}
          <div className="mb-9 flex justify-center animate-lc-fade-up md:mb-7">
            <Link
              href="/"
              aria-label="LiveContact home"
              className="inline-flex rounded-[10px] outline-none transition-transform duration-[160ms] ease-out-strong active:scale-[0.97] focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-4 focus-visible:ring-offset-[#F4F4F5] dark:focus-visible:ring-offset-[#20232A]"
            >
              <Logo height={40} className="h-10 w-auto md:h-8" />
            </Link>
          </div>

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={pathname}
              initial={{ opacity: 0, transform: `translateY(${lift}px)` }}
              animate={{
                opacity: 1,
                transform: 'translateY(0px)',
                transition: { duration: 0.26, ease: EASE_OUT },
              }}
              exit={{
                opacity: 0,
                transform: `translateY(${reduced ? 0 : -5}px)`,
                transition: { duration: 0.16, ease: EASE_OUT },
              }}
            >
              <div className="rounded-[24px] bg-surface p-7 shadow-surface md:p-5">
                <FrozenRouter>{children}</FrozenRouter>
              </div>
            </motion.div>
          </AnimatePresence>

          <p className="mt-7 text-center text-[12px] leading-[1.5] tracking-[-0.2px] text-ink-4 animate-lc-fade-up [animation-delay:120ms]">
            By continuing you agree to the{' '}
            <Link
              href="#"
              className="rounded-[4px] font-medium text-ink-3 underline-offset-2 outline-none transition-colors duration-[140ms] hover:text-ink hover:underline focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              Terms
            </Link>{' '}
            and{' '}
            <Link
              href="#"
              className="rounded-[4px] font-medium text-ink-3 underline-offset-2 outline-none transition-colors duration-[140ms] hover:text-ink hover:underline focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  )
}

export default LayoutGuestAuth
